<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import Input from "~/components/dumb/Input.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { copyAndNotify } from "~/components/notifications";
import { CheckIcon, CopyIcon, OpenExternalLinkIcon, RemoveLinkIcon, XIcon } from "~/icons";
import { normalizeUrl, validateUrl } from "~/utils/validation";

const props = defineProps<{
  url: string;
  nodeKey: string;
}>();

const emit = defineEmits<{
  save: [nodeKey: string, text: string | null];
}>();

const urlInput = ref<InstanceType<typeof Input> | null>(null);
const nodeKeyCopy = props.nodeKey;

const hasLink = computed(() => props.url !== "");

const isInputValid = computed(() => urlInput.value?.isValid ?? true);

const inputValue = computed(() => urlInput.value?.value ?? "");

const inputError = computed(() => {
  const validationResult = validateUrl(inputValue.value, true);
  return validationResult.isValid ? "" : (validationResult.error ?? "");
});

const stopEditing = () => {
  if (!urlInput.value || !isInputValid.value) {
    emit("save", nodeKeyCopy, props.url || null);
    return;
  }
  urlInput.value.blur();
  const normalizedValue = normalizeUrl(urlInput.value.value);
  emit("save", nodeKeyCopy, normalizedValue);
};

onMounted(() => {
  if (hasLink.value) {
    return;
  }
  urlInput.value?.focus();
});

onBeforeUnmount(() => {
  if (!urlInput.value) {
    return;
  }

  const { isValid, value } = urlInput.value;
  if (isValid) {
    if (value !== props.url && value !== "") {
      const normalizedValue = normalizeUrl(value);
      emit("save", nodeKeyCopy, normalizedValue);
    }
    return;
  }

  emit("save", nodeKeyCopy, props.url || null);
});
</script>

<template>
  <div class="flex size-full items-center gap-1 p-1 text-lt">
    <Input
      ref="urlInput"
      :init-value="url"
      label="Link URL"
      class="w-72"
      input-classes="!px-1 !py-0.5"
      hide-label
      hide-error
      :validate="(url) => validateUrl(url, true)"
      @click.stop
      @finalize="stopEditing" />
    <template v-if="!hasLink">
      <Tooltip text="Cancel">
        <button
          type="button"
          class="rounded-sm p-1 hover:bg-md"
          aria-label="Cancel"
          @click="emit('save', nodeKeyCopy, null)">
          <span class="sr-only">Cancel</span>
          <XIcon class="icon-sm" />
        </button>
      </Tooltip>
      <Tooltip :text="inputError || 'Confirm'">
        <button
          type="button"
          class="rounded-sm hover-bg-primary bg-primary-base p-1 text-white disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!isInputValid"
          aria-label="Confirm"
          @click="stopEditing">
          <span class="sr-only">Confirm</span>
          <CheckIcon class="icon-sm" />
        </button>
      </Tooltip>
    </template>
    <template v-else>
      <Tooltip text="Remove link">
        <button
          type="button"
          class="rounded-sm p-1 hover:bg-md"
          aria-label="Remove link"
          @click="emit('save', nodeKeyCopy, '')">
          <span class="sr-only">Remove link</span>
          <RemoveLinkIcon class="icon-sm" />
        </button>
      </Tooltip>
      <Tooltip text="Copy link">
        <button
          type="button"
          class="rounded-sm p-1 hover:bg-md"
          aria-label="Copy link"
          @click="copyAndNotify('Link URL', url)">
          <CopyIcon class="icon-sm" />
        </button>
      </Tooltip>
      <Tooltip text="Open link">
        <a :href="url" target="_blank" rel="noopener noreferrer" class="rounded-sm p-1 hover:bg-md">
          <span class="sr-only">Open link</span>
          <OpenExternalLinkIcon class="icon-sm" />
        </a>
      </Tooltip>
    </template>
  </div>
</template>
